// components/masterdata/KategorienListe.jsx

import React, {useState, useEffect, useCallback} from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router';
import Navbar from '../utils/Navbar';

const KategorienListe = () => {
  const { fetchWithAuth, accessToken } = useAuth();
  const navigate = useNavigate();

  const [kategorien, setKategorien] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newKategorie, setNewKategorie] = useState({
    name: "",
    name_abk: "",
    order: ""
  });

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchKategorien = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/data/kategorien`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Kategorien');
      }

      const data = await response.json();
      setKategorien(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const handleSave = async () => {
    try {
      for (const id in editData) {
        await fetchWithAuth(`/api/data/kategorien/${id}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editData[id]),
        });
      }
      setEditMode(false);
      fetchKategorien();
    } catch (err) {
      alert("Fehler beim Speichern: " + err.message);
    }
  };

  const handleDeleteSingle = async (id) => {
    if (!window.confirm("Willst du diese Kategorie wirklich löschen?")) return;

    try {
      await fetchWithAuth(`/api/data/kategorien/${id}/`, {
        method: "DELETE",
      });
      fetchKategorien(); // Liste neu laden
    } catch (err) {
      alert("Fehler beim Löschen: " + err.message);
    }
  };

  const handleAddKategorie = async () => {
    if (!newKategorie.name.trim()) return;

    try {
      const response = await fetchWithAuth(`/api/data/kategorien/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newKategorie)
      });

      if (response.ok) {
        setNewKategorie({ name: "", name_abk: "", order: "" });
        fetchKategorien();
      } else {
        const errorData = await response.json();

        // show error message
        if (errorData.detail) {
          alert("Fehler: " + errorData.detail);
        } else if (Array.isArray(errorData)) {
          alert("Fehler: " + errorData.join(", "));
        } else {
          const messages = Object.values(errorData).flat().join(", ");
          alert("Fehler: " + messages);
        }
      }
    } catch (err) {
      alert('Netzwerkfehler: ' + err.message);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    fetchKategorien();
  }, [accessToken, fetchKategorien]);

  if (loading) return <div>Lade Kategorien...</div>;
  if (error) return <div className="text-danger">Fehler: {error}</div>;

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h2 className="mb-4">Kategorien verwalten</h2>

        <div className="d-flex mb-4 gap-2">
          <input
            className="form-control"
            type="text"
            placeholder="Neue Kategorie..."
            value={newKategorie.name}
            onChange={(e) => setNewKategorie({ ...newKategorie, name: e.target.value })}
          />
          <input
            className="form-control"
            type="text"
            placeholder="Abkürzung"
            value={newKategorie.name_abk}
            onChange={(e) => setNewKategorie({ ...newKategorie, name_abk: e.target.value })}
          />
          <input
            className="form-control"
            type="text"
            placeholder="Sortierung"
            value={newKategorie.order}
            onChange={(e) => setNewKategorie({ ...newKategorie, order: e.target.value })}
          />
          <button className="btn btn-success" onClick={handleAddKategorie} disabled={editMode}>
            Hinzufügen
          </button>
        </div>

        {editMode && (
          <div className="alert alert-warning d-flex align-items-center" role="alert">
            <i className="bi bi-pencil-square me-2"></i>
            Bearbeitungsmodus aktiv – Änderungen noch nicht gespeichert!
          </div>
        )}

        <table className="table table-striped">
          <thead>
            <tr>
              {editMode && <th>Aktion</th>}
              <th>ID</th>
              <th>Name</th>
              <th>Abkürzung</th>
              <th>Sortierung</th>
            </tr>
          </thead>
          <tbody>
            {kategorien.map((kat) => (
              <tr key={kat.id}>
                {editMode && (
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteSingle(kat.id)}
                    >
                      🗑️
                    </button>
                  </td>
                )}
                <td>{kat.id}</td>

                {/* Name */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.name || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], name: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.name
                  )}
                </td>

                {/* Name Abk */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.name_abk || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], name_abk: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.name_abk
                  )}
                </td>

                {/* Order */}
                <td>
                  {editMode ? (
                    <input
                      type="number"
                      className="form-control"
                      value={editData[kat.id]?.order || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], order: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.order
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="d-flex flex-wrap gap-2 mb-4">
          {editMode ? (
            <button
              className="btn btn-warning"
              onClick={() => {
                setEditMode(false);
                setEditData({});
              }}
            >
              Abbrechen
            </button>
          ) : (
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/objekte")}
            >
              Zurück
            </button>
          )}

          {!editMode && (
            <button className="btn btn-primary" onClick={() => {
              setEditMode(true);
              const initialData = {};
              kategorien.forEach(k => {
                initialData[k.id] = {
                  name: k.name,
                  name_abk: k.name_abk,
                  order: k.order
                };
              });
              setEditData(initialData);
            }}>
              Bearbeiten
            </button>
          )}

          {editMode && (
              <button className="btn btn-success" onClick={handleSave}>
                Änderungen speichern
              </button>
          )}
        </div>
      </div>
    </>
  );
};

export default KategorienListe;
